import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('flirtylive_token'));
  const [isAgeVerified, setIsAgeVerified] = useState(() => localStorage.getItem('flirtylive_age') === 'true');
  const [loading, setLoading] = useState(true);

  // Set auth header
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch {
        localStorage.removeItem('flirtylive_token');
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    checkSession();
  }, [token]);

  const saveAuth = useCallback((userData, tokenVal) => {
    setUser(userData);
    setToken(tokenVal);
    localStorage.setItem('flirtylive_token', tokenVal);
    localStorage.setItem('flirtylive_user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${tokenVal}`;
  }, []);

  const register = useCallback(async (phone, password, username) => {
    const res = await api.post('/auth/register', { phone, password, username });
    saveAuth(res.data.user, res.data.token);
    return res.data;
  }, [saveAuth]);

  const verifySMS = useCallback(async (phone, code) => {
    const res = await api.post('/auth/verify-sms', { phone, code });
    if (res.data.token) saveAuth(res.data.user, res.data.token);
    return res.data;
  }, [saveAuth]);

  const login = useCallback(async (loginVal, password) => {
    const res = await api.post('/auth/login', { login: loginVal, password });
    saveAuth(res.data.user, res.data.token);
    return res.data;
  }, [saveAuth]);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('flirtylive_token');
    localStorage.removeItem('flirtylive_user');
    delete api.defaults.headers.common['Authorization'];
  }, []);

  const verifyAge = useCallback(() => {
    setIsAgeVerified(true);
    localStorage.setItem('flirtylive_age', 'true');
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isAgeVerified, loading, login, register, verifySMS, logout, verifyAge, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
